/**
 * Pure formatting for the studies report: modality names, growth wording, the
 * server's bucket rule, and one CSV builder per table.
 *
 * Kept apart from the page so the page only wires buttons, and so every rule
 * that decides what a number says lives in one place:
 *
 * * **Null is not zero.** A figure the catalogue has not computed is null in
 *   the response, renders as a dash and exports as an empty cell — never a 0
 *   someone later sums.
 * * **An incomplete figure never travels alone.** Each CSV whose numbers can
 *   be provisional carries the marker that says so (`provisional`,
 *   `new_returning_excludes`, a per-row `pending`), so a file passed on by
 *   email keeps the caveat the page showed.
 */

import { addDays, daysInclusive, isDay } from '../../lib/baghdad';
import type { CsvCell } from '../../lib/csv';
import { formatNumber } from '../../lib/format';
import type {
  StudyBasis,
  StudyCompare,
  StudyGrowth,
  StudyReportResponse,
  StudyTotalsGrowth,
  UploadBucket,
} from '../../types/api';

// ── modalities ───────────────────────────────────────────────────────────────

/** DICOM Modality codes → the names a reader knows them by. */
export const MODALITY_NAMES: Readonly<Record<string, string>> = {
  CT: 'CT',
  MR: 'MRI',
  CR: 'Computed radiography',
  DX: 'Digital X-ray',
  MG: 'Mammography',
  US: 'Ultrasound',
  PT: 'PET',
  NM: 'Nuclear medicine',
  XA: 'Angiography',
  RF: 'Fluoroscopy',
  PX: 'Panoramic X-ray',
  IO: 'Intra-oral X-ray',
  OP: 'Ophthalmic photography',
  OPT: 'Ophthalmic tomography (OCT)',
  XC: 'External camera photography',
  ES: 'Endoscopy',
  SM: 'Slide microscopy',
  ECG: 'ECG',
  PR: 'Presentation state',
  SR: 'Structured report',
  KO: 'Key object selection',
  DOC: 'Document',
  OT: 'Other',
  SEG: 'Segmentation',
  REG: 'Registration',
  RTSTRUCT: 'RT structure set',
  MULTI: 'Several modalities',
};

const NO_MODALITY = 'Unknown (no Modality tag)';

/** The parts of an exam label: `CT+PT` → `['CT', 'PT']`. */
function modalityParts(code: string): string[] {
  return code.split('+').filter(Boolean);
}

/** A short label for a legend or a chart axis: `CT`, `CT + PT`, `Unknown`. */
export function modalityShort(code: string): string {
  if (!code) return 'Unknown';
  return modalityParts(code).join(' + ');
}

/**
 * The label with its full name: `MR (MRI)`, `CT + PT (CT, PET)`, `CT`.
 *
 * A code whose name is itself (CT, ECG) is not repeated, an unknown code
 * shows as the code, and the empty label (no Modality tag) says so in words.
 */
export function modalityDisplay(code: string): string {
  if (!code) return NO_MODALITY;
  const parts = modalityParts(code);
  if (parts.length > 1) {
    return `${parts.join(' + ')} (${parts.map((p) => MODALITY_NAMES[p] ?? p).join(', ')})`;
  }
  const name = MODALITY_NAMES[code];
  return name && name !== code ? `${code} (${name})` : code;
}

/** "Presentation state" → "presentation state", but "RT structure set" stays. */
function lowerFirst(text: string): string {
  return /^[A-Z][a-z]/.test(text) ? `${text[0]!.toLowerCase()}${text.slice(1)}` : text;
}

/**
 * The footnote under "Studies by type" for one attached-object modality:
 * "PR (presentation state): present in 26 studies; attached to those exams,
 * not counted as separate studies, and the only content of 3 studies (shown
 * as PR above)".
 */
export function attachedFootnote(row: {
  modality: string;
  studies: number;
  attached_only: number;
}): string {
  const name = MODALITY_NAMES[row.modality];
  const head = name && name !== row.modality ? `${row.modality} (${lowerFirst(name)})` : row.modality || NO_MODALITY;
  let text = `${head}: present in ${countText(row.studies, 'study', 'studies')}; attached to those exams, not counted as separate studies`;
  if (row.attached_only > 0) {
    text += `, and the only content of ${countText(row.attached_only, 'study', 'studies')} (shown as ${modalityShort(row.modality)} above)`;
  }
  return text;
}

function countText(count: number, singular: string, plural: string): string {
  return `${formatNumber(count)} ${count === 1 ? singular : plural}`;
}

// ── words for the controls' values ───────────────────────────────────────────

export function basisNoun(basis: StudyBasis): string {
  return basis === 'exam' ? 'exam date' : 'received date';
}

export function otherBasis(basis: StudyBasis): StudyBasis {
  return basis === 'exam' ? 'received' : 'exam';
}

// ── growth ───────────────────────────────────────────────────────────────────

/** A change as "▲ 12 %" / "▼ 3.5 %" / "0 %": tone-free, the arrow is the sign. */
export function changeText(change: number): string {
  const pct = Math.abs(change * 100);
  const digits = pct < 10 ? 1 : 0;
  const rounded = pct.toFixed(digits);
  if (Number(rounded) === 0) return '0 %';
  return `${change > 0 ? '▲' : '▼'} ${rounded} %`;
}

/**
 * One headline tile's growth hint, or null when there is nothing to compare
 * (no growth block at all: the page must not invent a comparison).
 *
 * `title` carries the full `reason` when the comparison leaves centers out,
 * so the tile stays one line and the reader can still find out which.
 */
export function growthHint(
  metric: StudyGrowth,
  growth: StudyTotalsGrowth | null,
): { text: string; title?: string } | null {
  if (!growth) return null;
  if (!growth.covered) return { text: `no comparison: ${growth.reason}` };
  const windows = `${growth.current_from}–${growth.current_to} vs ${growth.previous_from}–${growth.previous_to}`;
  let text =
    metric.change !== null
      ? `${changeText(metric.change)} (${windows})`
      : metric.previous === 0
        ? `none in ${growth.previous_from}–${growth.previous_to} to compare with`
        : `no comparison (${windows})`;
  if (growth.excluded.length > 0) {
    const n = growth.excluded.length;
    text += ` · excl. ${n} ${n === 1 ? 'center' : 'centers'}`;
    return { text, title: growth.reason };
  }
  return { text };
}

/** The short "vs previous" cell of a table row. */
export function changeCell(metric: StudyGrowth, growth: StudyTotalsGrowth | null): string {
  if (!growth || !growth.covered) return '—';
  if (metric.change !== null) return changeText(metric.change);
  return metric.previous === 0 ? 'new' : '—';
}

// ── buckets (the server's rule, so a disabled option agrees with a 400) ─────

/** The axis cap: the server refuses a report with more buckets than this. */
export const MAX_BUCKETS = 500;

/** Monday of the ISO week `day` falls in. */
function mondayOf(day: string): string {
  const weekday = (new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7;
  return addDays(day, -weekday);
}

/**
 * How many buckets the dense axis `from`..`to` holds — the length of the
 * server's `iter_buckets(from, to, bucket)`: every bucket touching the range,
 * weeks starting on Monday, months on the 1st.
 */
export function bucketCount(from: string, to: string, bucket: UploadBucket): number {
  if (!isDay(from) || !isDay(to) || from > to) return 0;
  switch (bucket) {
    case 'day':
      return daysInclusive(from, to);
    case 'week':
      return (daysInclusive(mondayOf(from), mondayOf(to)) - 1) / 7 + 1;
    case 'month': {
      const [y1, m1] = from.split('-').map(Number) as [number, number];
      const [y2, m2] = to.split('-').map(Number) as [number, number];
      return (y2 - y1) * 12 + (m2 - m1) + 1;
    }
  }
}

// ── the request ──────────────────────────────────────────────────────────────

export interface StudyReportQuery {
  from: string;
  to: string;
  basis: StudyBasis;
  bucket: UploadBucket;
  compare: StudyCompare;
  slug: string | null;
}

/** `GET /api/reports/studies` for these controls. */
export function studyReportPath(q: StudyReportQuery): string {
  const qs = new URLSearchParams({
    from: q.from,
    to: q.to,
    basis: q.basis,
    bucket: q.bucket,
    compare: q.compare,
  });
  if (q.slug) qs.set('slug', q.slug);
  return `/api/reports/studies?${qs.toString()}`;
}

// ── CSV ──────────────────────────────────────────────────────────────────────

/** A CSV as `[header, rows]`, ready for `downloadCsv`. */
export type CsvTable = [header: string[], rows: CsvCell[][]];

/** `studies-{slug|fleet}-{basis}-{what}-{from}-to-{to}.csv`, from the response
 *  itself, so a file is always named after the numbers inside it. */
export function studyCsvName(r: StudyReportResponse, what: string): string {
  return `studies-${r.slug || 'fleet'}-${r.basis}-${what}-${r.from_day}-to-${r.to_day}.csv`;
}

function yesOrNo(value: boolean): string {
  return value ? 'yes' : 'no';
}

/** Figures may be incomplete for some center in scope. */
function provisional(r: StudyReportResponse): string {
  return yesOrNo(r.pending_slugs.length > 0);
}

const GROWTH_METRICS = ['studies', 'patients', 'series', 'instances', 'bytes'] as const;

/** One row: the headline, the notes under it, and the comparison. */
export function totalsCsv(r: StudyReportResponse): CsvTable {
  const g = r.growth;
  return [
    [
      'from',
      'to',
      'basis',
      'studies',
      'patients',
      'series',
      'instances',
      'bytes',
      'first_day',
      'last_day',
      'exam_date_min',
      'exam_date_max',
      'exam_date_fallback',
      'deleted_excluded',
      'undated',
      'skipped_studies',
      'other_basis_studies',
      'stored_now_bytes',
      'stored_now_as_of',
      'growth_compare',
      'growth_covered',
      'growth_current_from',
      'growth_current_to',
      'growth_previous_from',
      'growth_previous_to',
      ...GROWTH_METRICS.flatMap((m) => [`${m}_previous`, `${m}_change`]),
      'growth_excluded',
      'provisional',
    ],
    [
      [
        r.from_day,
        r.to_day,
        r.basis,
        r.totals.studies,
        r.totals.patients,
        r.totals.series,
        r.totals.instances,
        r.totals.bytes,
        r.totals.first_day,
        r.totals.last_day,
        r.totals.exam_date_min,
        r.totals.exam_date_max,
        r.exam_date_fallback,
        r.deleted_excluded,
        r.undated,
        r.skipped_studies,
        r.other_basis_studies,
        r.stored_now.bytes,
        r.stored_now.as_of,
        g?.compare ?? null,
        g ? yesOrNo(g.covered) : null,
        g?.current_from ?? null,
        g?.current_to ?? null,
        g?.previous_from ?? null,
        g?.previous_to ?? null,
        ...GROWTH_METRICS.flatMap((m) => [g?.[m].previous ?? null, g?.[m].change ?? null]),
        g ? g.excluded.join(' ') : null,
        provisional(r),
      ],
    ],
  ];
}

export function byTypeCsv(r: StudyReportResponse): CsvTable {
  return [
    [
      'modality',
      'label',
      'studies',
      'share',
      'patients',
      'series',
      'instances',
      'bytes',
      'previous_studies',
      'change',
      'provisional',
    ],
    r.by_modality.map((row) => [
      row.modality,
      modalityDisplay(row.modality),
      row.studies,
      row.share,
      row.patients,
      row.series,
      row.instances,
      row.bytes,
      row.growth.previous,
      row.growth.change,
      provisional(r),
    ]),
  ];
}

export function attachedCsv(r: StudyReportResponse): CsvTable {
  return [
    ['modality', 'label', 'studies_present_in', 'attached_only'],
    r.attached.map((row) => [row.modality, modalityDisplay(row.modality), row.studies, row.attached_only]),
  ];
}

/** One row per bucket, one column per exam label (in `modalities` order). */
export function perBucketCsv(r: StudyReportResponse): CsvTable {
  return [
    [
      'bucket',
      'bucket_end',
      'partial',
      'studies',
      ...r.modalities.map((m) => m || 'unknown'),
      'patients',
      'series',
      'instances',
      'bytes',
      'provisional',
    ],
    r.buckets.map((b) => [
      b.bucket,
      b.bucket_end,
      yesOrNo(b.partial),
      b.studies,
      ...r.modalities.map((m) => b.by_modality[m] ?? 0),
      b.patients,
      b.series,
      b.instances,
      b.bytes,
      provisional(r),
    ]),
  ];
}

export function lagCsv(r: StudyReportResponse): CsvTable {
  const total = r.lag.reduce((sum, row) => sum + row.studies, 0);
  return [
    ['key', 'label', 'studies', 'share'],
    r.lag.map((row) => [row.key, row.label, row.studies, total > 0 ? row.studies / total : null]),
  ];
}

export function patientsCsv(r: StudyReportResponse): CsvTable {
  const p = r.patients;
  return [
    [
      'patients',
      'new',
      'returning',
      'repeat',
      'repeat_studies',
      'max_studies',
      'studies_without_patient',
      'provisional',
      'new_returning_excludes',
    ],
    [
      [
        p.patients,
        p.new,
        p.returning,
        p.repeat,
        p.repeat_studies,
        p.max_studies,
        p.studies_without_patient,
        yesOrNo(!p.history_complete),
        p.history_centers_excluded.join(' '),
      ],
    ],
  ];
}

export function centersCsv(r: StudyReportResponse): CsvTable {
  const reasons = new Map(r.catalogue.map((c) => [c.slug, c.pending_reason]));
  return [
    [
      'slug',
      'name',
      'studies',
      'share',
      'patients',
      'series',
      'instances',
      'bytes',
      'exam_date_fallback',
      'deleted_excluded',
      'skipped_studies',
      'pending',
      'pending_reason',
    ],
    r.centers.map((c) => [
      c.slug,
      c.name,
      c.studies,
      c.share,
      c.patients,
      c.series,
      c.instances,
      c.bytes,
      c.exam_date_fallback,
      c.deleted_excluded,
      c.skipped_studies,
      yesOrNo(c.pending),
      c.pending ? reasons.get(c.slug) ?? null : null,
    ]),
  ];
}

export function descriptionsCsv(r: StudyReportResponse): CsvTable {
  return [
    ['rank', 'description', 'studies', 'share', 'modality'],
    (r.top_descriptions ?? []).map((d, index) => [
      index + 1,
      d.description,
      d.studies,
      d.share,
      d.modality,
    ]),
  ];
}

export function catalogueCsv(r: StudyReportResponse): CsvTable {
  return [
    [
      'slug',
      'name',
      'database_name',
      'backfill_complete',
      'catalogued',
      'index_studies',
      'skipped_studies',
      'read_timeouts',
      'held_deletions',
      'last_success_at',
      'last_reconciled_day',
      'stale',
      'errors',
      'last_error_kind',
      'last_error',
      'pending',
      'pending_reason',
    ],
    r.catalogue.map((c) => [
      c.slug,
      c.name,
      c.database_name,
      yesOrNo(c.backfill_complete),
      c.catalogued,
      c.index_studies,
      c.skipped_studies,
      c.read_timeouts,
      c.held_deletions,
      c.last_success_at,
      c.last_reconciled_day,
      yesOrNo(c.stale),
      c.errors,
      c.last_error_kind,
      c.last_error,
      yesOrNo(c.pending),
      c.pending_reason,
    ]),
  ];
}
